import { useState, useCallback } from "react";
import axios from "axios";
import Auth from "../../Auth/Auth";
import {
  FileUploadState,
  UploadResponse,
  FileUploadConfig,
  UploadUserInfo,
  WebdavAddressResponse
} from "../../components/upload-files/types";
import {
  validateFiles,
  mergeFileList,
  removeFileFromList,
  DEFAULT_CONFIG
} from "../../components/upload-files/utils";

// 1. employeeId, departmentId, uuid를 backend로 전달
// 2. 위 값을 가지고 folder를 생성해 파일을 업로드
// 3. 파일 업로드 완료 후, 위 값을 다시 frontend로 전달
// 4. 결재 상신 또는 다른 작업 시 위 값을 다시 backend로 전달하여 처리
export const useFileUpload = (
  config: FileUploadConfig = DEFAULT_CONFIG,
  setUserInfo: (userInfo: UploadUserInfo) => void = () => {},
  uuid: string = ""
) => {
  const [state, setState] = useState<FileUploadState>({
    selectedFiles: null,
    uploadProgress: 0,
    isUploading: false,
    isDragOver: false,
    validationErrors: []
  });

  const updateState = useCallback((updates: Partial<FileUploadState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const validateAndSetFiles = useCallback(
    (files: FileList) => {
      const { validFiles, errors } = validateFiles(files, config);

      if (errors.length > 0) {
        updateState({ validationErrors: errors });
        return false;
      }

      updateState({ validationErrors: [] });
      return true;
    },
    [config, updateState]
  );

  const uploadNewFiles = useCallback(
    async (filesToUpload: File[]): Promise<boolean> => {
      updateState({ isUploading: true, uploadProgress: 0 });

      const formData = new FormData();
      Array.from(filesToUpload).forEach((file: File, index: number) => {
        formData.append(`file${index}`, file);
      });
      formData.append("uuid", uuid);

      try {
        const response = await axios.post<UploadResponse>(
          config.uploadUrl,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              token: Auth.getUserToken()
            },
            onUploadProgress: (progressEvent: any) => {
              if (progressEvent.total) {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                updateState({ uploadProgress: percentCompleted });
              }
            }
          }
        );

        if (response.data.ok) {
          console.log("업로드된 파일들:", response.data.uploadedFiles);
          setUserInfo({
            employeeId: response.data.employeeId ?? "",
            departmentId: response.data.departmentId ?? "",
            uuid: response.data.uuid ?? ""
          });
          return true;
        } else {
          alert(`업로드 실패: ${response.data.error}`);
          return false;
        }
      } catch (error) {
        console.error("업로드 오류:", error);
        if (axios.isAxiosError(error)) {
          const errorMessage =
            (error as any).response?.data?.error ||
            "업로드 중 오류가 발생했습니다.";
          alert(`업로드 실패: ${errorMessage}`);
        } else {
          alert("업로드 중 오류가 발생했습니다.");
        }
        return false;
      } finally {
        updateState({ isUploading: false, uploadProgress: 0 });
      }
    },
    [config, updateState]
  );

  const addFiles = useCallback(
    async (newFiles: FileList) => {
      if (!validateAndSetFiles(newFiles)) {
        return false;
      }

      const { validFiles } = validateFiles(newFiles, config);

      // 자동 업로드 실행
      const uploadSuccess = await uploadNewFiles(validFiles);

      if (uploadSuccess) {
        const mergedFiles = mergeFileList(state.selectedFiles, validFiles);

        const { validFiles: finalValidFiles, errors: mergedErrors } =
          validateFiles(mergedFiles, config);

        if (mergedErrors.length > 0) {
          updateState({ validationErrors: mergedErrors });
          return false;
        }

        updateState({ selectedFiles: mergedFiles, validationErrors: [] });
        return true;
      } else {
        return false;
      }
    },
    [
      state.selectedFiles,
      config,
      validateAndSetFiles,
      updateState,
      uploadNewFiles
    ]
  );

  const getWebdavAddress = useCallback(async (): Promise<boolean> => {
    try {
      const response = await axios.get<WebdavAddressResponse>(
        config.webdavAddress,
        {
          headers: {
            token: Auth.getUserToken()
          }
        }
      );

      if (response.data.ok) {
        return response.data.webDAVAddressExists;
      }
      return false;
    } catch (error) {
      console.error("웹DAV 주소 조회 오류:", error);
      return false;
    }
  }, [config]);

  const removeFile = useCallback(
    async (indexToRemove: number) => {
      if (!state.selectedFiles) return;

      // indexToRemove로 이름을 구한다.
      // /api/updown-files/delete로 삭제
      const fileName = Array.from(state.selectedFiles)[indexToRemove].name;
      try {
        const response = await axios.post<UploadResponse>(
          config.deleteUrl,
          {
            fileName,
            uuid
          },
          {
            headers: {
              "Content-Type": "application/json",
              token: Auth.getUserToken()
            }
          }
        );
        if (response.data.ok) {
          const updatedFiles = removeFileFromList(
            state.selectedFiles,
            indexToRemove
          );
          updateState({
            selectedFiles: updatedFiles.length > 0 ? updatedFiles : null,
            validationErrors: []
          });
        }
      } catch (error) {
        console.error("파일 삭제 오류:", error);
      }
    },
    [state.selectedFiles, updateState]
  );

  const setDragOver = useCallback(
    (isDragOver: boolean) => {
      updateState({ isDragOver });
    },
    [updateState]
  );

  const uploadFiles = useCallback(async (): Promise<boolean> => {
    if (!state.selectedFiles || state.selectedFiles.length === 0) {
      alert("파일을 선택해주세요.");
      return false;
    }

    // 업로드 전 최종 검증
    const { validFiles, errors } = validateFiles(state.selectedFiles, config);
    if (errors.length > 0) {
      updateState({ validationErrors: errors });
      return false;
    }

    updateState({ isUploading: true, uploadProgress: 0 });

    const formData = new FormData();
    Array.from(state.selectedFiles).forEach((file: File, index: number) => {
      formData.append(`file${index}`, file);
    });
    formData.append("uuid", uuid);

    try {
      const response = await axios.post<UploadResponse>(
        config.uploadUrl,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            token: Auth.getUserToken()
          },
          onUploadProgress: (progressEvent: any) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              updateState({ uploadProgress: percentCompleted });
            }
          }
        }
      );

      if (response.data.ok) {
        console.log("업로드된 파일들:", response.data.uploadedFiles);
        setUserInfo({
          employeeId: response.data.employeeId ?? "",
          departmentId: response.data.departmentId ?? "",
          uuid: response.data.uuid ?? ""
        });
        updateState({
          uploadProgress: 0,
          selectedFiles: null,
          validationErrors: []
        });
        return true;
      } else {
        alert(`업로드 실패: ${response.data.error}`);
        return false;
      }
    } catch (error) {
      console.error("업로드 오류:", error);
      if (axios.isAxiosError(error)) {
        const errorMessage =
          (error as any).response?.data?.error ||
          "업로드 중 오류가 발생했습니다.";
        alert(`업로드 실패: ${errorMessage}`);
      } else {
        alert("업로드 중 오류가 발생했습니다.");
      }
      return false;
    } finally {
      updateState({ isUploading: false, uploadProgress: 0 });
    }
  }, [state.selectedFiles, config, updateState]);

  const clearFiles = useCallback(() => {
    updateState({
      selectedFiles: null,
      validationErrors: [],
      uploadProgress: 0
    });
  }, [updateState]);

  return {
    ...state,
    addFiles,
    removeFile,
    setDragOver,
    uploadFiles,
    clearFiles,
    config,
    getWebdavAddress
  };
};
